import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Bell,
  CheckCircle2,
  Clock,
  LayoutGrid,
  ShieldCheck,
  Smartphone,
  Wifi,
  X,
} from "lucide-react";

type AccountPreferencesPanelProps = {
  open: boolean;
  onClose: () => void;
  officerEmail?: string;
};

type AccountPreferenceState = {
  notifications: {
    emailAlerts: boolean;
    smsEscalations: boolean;
    weeklySummary: boolean;
    shiftReminders: boolean;
    complianceAlerts: boolean;
  };
  security: {
    loginAlerts: boolean;
    enforceMfa: boolean;
    biometricLogin: boolean;
    rememberDevice: boolean;
    sessionTimeout: string;
  };
  workspace: {
    theme: "system" | "light" | "dark";
    density: "comfortable" | "compact";
    autoRefresh: boolean;
    showBeta: boolean;
  };
  records: {
    shareDailyBriefing: boolean;
    escalateIncidents: boolean;
    includeOpsDigest: boolean;
  };
};

const defaultPreferences: AccountPreferenceState = {
  notifications: {
    emailAlerts: true,
    smsEscalations: true,
    weeklySummary: true,
    shiftReminders: true,
    complianceAlerts: false,
  },
  security: {
    loginAlerts: true,
    enforceMfa: true,
    biometricLogin: false,
    rememberDevice: false,
    sessionTimeout: "30",
  },
  workspace: {
    theme: "system",
    density: "comfortable",
    autoRefresh: true,
    showBeta: false,
  },
  records: {
    shareDailyBriefing: true,
    escalateIncidents: true,
    includeOpsDigest: false,
  },
};

function createPreferencesState(): AccountPreferenceState {
  return {
    notifications: { ...defaultPreferences.notifications },
    security: { ...defaultPreferences.security },
    workspace: { ...defaultPreferences.workspace },
    records: { ...defaultPreferences.records },
  };
}

const connectedApps = [
  {
    id: "mobile",
    name: "PHMIS Mobile Capture",
    description: "Offline inspections and traveller screening handover.",
    status: "Active",
    lastSync: "09 Mar 2025 • 07:55 CAT",
  },
  {
    id: "analytics",
    name: "Analytics Workspace",
    description: "Power BI dashboards for Port Health command centre.",
    status: "Active",
    lastSync: "08 Mar 2025 • 20:10 CAT",
  },
  {
    id: "handover",
    name: "Operations Handover Bot",
    description: "Microsoft Teams bot posting nightly duty summaries.",
    status: "Paused",
    lastSync: "08 Mar 2025 • 19:00 CAT",
  },
];

export function AccountPreferencesPanel({ open, onClose, officerEmail }: AccountPreferencesPanelProps) {
  const [preferences, setPreferences] = useState<AccountPreferenceState>(() => createPreferencesState());
  const [status, setStatus] = useState<"idle" | "saving" | "saved">("idle");
  const [lastSaved, setLastSaved] = useState<Date>(new Date("2025-03-08T17:15:00Z"));
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (open) {
      setStatus("idle");
    }
  }, [open]);

  useEffect(
    () => () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
      }
    },
    [],
  );

  const activeNotificationCount = useMemo(
    () => Object.values(preferences.notifications).filter(Boolean).length,
    [preferences.notifications],
  );
  const totalNotificationCount = useMemo(
    () => Object.keys(preferences.notifications).length,
    [preferences.notifications],
  );

  const securityWarnings = useMemo(() => {
    const warnings: string[] = [];
    if (!preferences.security.enforceMfa) {
      warnings.push("MFA is disabled. Required for admin and outbreak modules.");
    }
    if (!preferences.security.loginAlerts) {
      warnings.push("Login alerts disabled. Compliance requires audit notifications.");
    }
    if (!preferences.security.sessionTimeout || Number(preferences.security.sessionTimeout) < 15) {
      warnings.push("Session timeout is below the recommended 15 minute threshold.");
    }
    return warnings;
  }, [preferences.security]);

  const channelSummary = useMemo(() => {
    const channels: string[] = [];
    if (preferences.notifications.emailAlerts) channels.push("Email");
    if (preferences.notifications.smsEscalations) channels.push("SMS");
    if (preferences.notifications.shiftReminders) channels.push("Duty roster push");
    if (preferences.notifications.weeklySummary) channels.push("Weekly digest");
    if (preferences.notifications.complianceAlerts) channels.push("Compliance alerts");
    return channels.join(" • ");
  }, [preferences.notifications]);

  function updateNotification(field: keyof AccountPreferenceState["notifications"]) {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      setPreferences((prev) => ({
        ...prev,
        notifications: { ...prev.notifications, [field]: checked },
      }));
    };
  }

  function updateSecurity<K extends keyof AccountPreferenceState["security"]>(field: K) {
    return (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value =
        event.target.type === "checkbox" ? (event.target as HTMLInputElement).checked : event.target.value;
      setPreferences((prev) => ({
        ...prev,
        security: { ...prev.security, [field]: value as AccountPreferenceState["security"][K] },
      }));
    };
  }

  function updateWorkspace<K extends keyof AccountPreferenceState["workspace"]>(field: K) {
    return (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value =
        event.target.type === "checkbox" ? (event.target as HTMLInputElement).checked : event.target.value;
      setPreferences((prev) => ({
        ...prev,
        workspace: { ...prev.workspace, [field]: value as AccountPreferenceState["workspace"][K] },
      }));
    };
  }

  function updateRecords<K extends keyof AccountPreferenceState["records"]>(field: K) {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      setPreferences((prev) => ({
        ...prev,
        records: { ...prev.records, [field]: checked },
      }));
    };
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("saving");

    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
    }

    saveTimer.current = setTimeout(() => {
      setStatus("saved");
      setLastSaved(new Date());
    }, 900);
  }

  function handleReset() {
    setPreferences(createPreferencesState());
    setStatus("idle");
  }

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-slate-900/50 px-4 py-8"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl rounded-3xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
          <div className="space-y-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
              <Bell className="h-4 w-4" /> Account Preferences
            </span>
            <h2 className="text-xl font-semibold text-slate-900">Notification & security controls</h2>
            <p className="max-w-2xl text-sm text-slate-600">
              Tailor how Port Health MIS keeps you informed. Preferences sync to mobile, Teams handovers and audit exports linked to {officerEmail ?? "your account"}.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2 text-xs text-slate-500">
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-600">
              <Clock className="h-3.5 w-3.5" /> Last saved {lastSaved.toLocaleString()}
            </span>
            {status === "saving" && (
              <span className="inline-flex items-center gap-2 rounded-full bg-indigo-100 px-3 py-1 font-medium text-indigo-700">
                <ShieldCheck className="h-3.5 w-3.5" /> Saving updates…
              </span>
            )}
            {status === "saved" && (
              <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 font-medium text-emerald-700">
                <CheckCircle2 className="h-3.5 w-3.5" /> Preferences saved
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100"
            aria-label="Close account preferences"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="grid gap-6 px-6 py-6 lg:grid-cols-[2fr,1fr]">
          <div className="space-y-6">
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <Bell className="h-4 w-4 text-blue-500" /> Notification delivery
              </div>
              <p className="mt-2 text-sm text-slate-600">Decide how inspection assignments, escalations, and digests reach you.</p>
              <div className="mt-4 space-y-3">
                <label className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-700">
                  <span>
                    Email alerts
                    <span className="block text-xs text-slate-500">Screening approvals, role changes, and assigned inspections.</span>
                  </span>
                  <input type="checkbox" checked={preferences.notifications.emailAlerts} onChange={updateNotification("emailAlerts")} />
                </label>
                <label className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-700">
                  <span>
                    SMS escalations
                    <span className="block text-xs text-slate-500">Critical incidents and quarantine referrals when email is delayed.</span>
                  </span>
                  <input type="checkbox" checked={preferences.notifications.smsEscalations} onChange={updateNotification("smsEscalations")} />
                </label>
                <label className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-700">
                  <span>
                    Weekly operations digest
                    <span className="block text-xs text-slate-500">Summary of SOP updates, analytics anomalies, and compliance tips.</span>
                  </span>
                  <input type="checkbox" checked={preferences.notifications.weeklySummary} onChange={updateNotification("weeklySummary")} />
                </label>
                <label className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-700">
                  <span>
                    Shift reminders
                    <span className="block text-xs text-slate-500">Receive roster reminders 30 minutes before duty start.</span>
                  </span>
                  <input type="checkbox" checked={preferences.notifications.shiftReminders} onChange={updateNotification("shiftReminders")} />
                </label>
                <label className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-700">
                  <span>
                    Compliance alerts
                    <span className="block text-xs text-slate-500">Notify on policy overrides or expiring licences you manage.</span>
                  </span>
                  <input type="checkbox" checked={preferences.notifications.complianceAlerts} onChange={updateNotification("complianceAlerts")} />
                </label>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <ShieldCheck className="h-4 w-4 text-emerald-500" /> Security & sign-in
              </div>
              <p className="mt-2 text-sm text-slate-600">Strengthen account protection for high-risk workflows.</p>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={preferences.security.enforceMfa}
                    onChange={updateSecurity("enforceMfa")}
                    className="mt-1"
                  />
                  <span>
                    Multi-factor authentication
                    <span className="block text-xs text-slate-500">Required for finance, licensing, and outbreak modules.</span>
                  </span>
                </label>
                <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={preferences.security.loginAlerts}
                    onChange={updateSecurity("loginAlerts")}
                    className="mt-1"
                  />
                  <span>
                    Login alerts
                    <span className="block text-xs text-slate-500">Receive an email if sign-in occurs from a new device.</span>
                  </span>
                </label>
                <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={preferences.security.biometricLogin}
                    onChange={updateSecurity("biometricLogin")}
                    className="mt-1"
                  />
                  <span>
                    Biometric sign-in (mobile)
                    <span className="block text-xs text-slate-500">Use device biometrics for PHMIS Mobile authentication.</span>
                  </span>
                </label>
                <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={preferences.security.rememberDevice}
                    onChange={updateSecurity("rememberDevice")}
                    className="mt-1"
                  />
                  <span>
                    Remember trusted device
                    <span className="block text-xs text-slate-500">Skip MFA for the next 30 days on this workstation.</span>
                  </span>
                </label>
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <label className="text-sm">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Session timeout (minutes)</span>
                  <select
                    value={preferences.security.sessionTimeout}
                    onChange={updateSecurity("sessionTimeout")}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  >
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="45">45 minutes</option>
                    <option value="60">60 minutes</option>
                  </select>
                </label>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <LayoutGrid className="h-4 w-4 text-purple-500" /> Workspace experience
              </div>
              <p className="mt-2 text-sm text-slate-600">Tune the interface for operations centre screens or mobile duty use.</p>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <label className="text-sm">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Theme</span>
                  <select
                    value={preferences.workspace.theme}
                    onChange={updateWorkspace("theme")}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-100"
                  >
                    <option value="system">Follow device (light/dark)</option>
                    <option value="light">Always light</option>
                    <option value="dark">Always dark</option>
                  </select>
                </label>
                <label className="text-sm">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Density</span>
                  <select
                    value={preferences.workspace.density}
                    onChange={updateWorkspace("density")}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-100"
                  >
                    <option value="comfortable">Comfortable (default)</option>
                    <option value="compact">Compact (data-dense)</option>
                  </select>
                </label>
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={preferences.workspace.autoRefresh}
                    onChange={updateWorkspace("autoRefresh")}
                    className="mt-1"
                  />
                  <span>
                    Auto-refresh dashboards
                    <span className="block text-xs text-slate-500">Refresh analytics cards every 5 minutes when docked.</span>
                  </span>
                </label>
                <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={preferences.workspace.showBeta}
                    onChange={updateWorkspace("showBeta")}
                    className="mt-1"
                  />
                  <span>
                    Preview beta modules
                    <span className="block text-xs text-slate-500">Access pilot features shared by HQ analytics.</span>
                  </span>
                </label>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <Clock className="h-4 w-4 text-amber-500" /> Handover & digests
              </div>
              <p className="mt-2 text-sm text-slate-600">Control what appears in the daily briefings sent to supervisors.</p>
              <div className="mt-4 space-y-3">
                <label className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-700">
                  <span>
                    Share daily shift briefing
                    <span className="block text-xs text-slate-500">Include your roster summary in the nightly Teams briefing.</span>
                  </span>
                  <input type="checkbox" checked={preferences.records.shareDailyBriefing} onChange={updateRecords("shareDailyBriefing")} />
                </label>
                <label className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-700">
                  <span>
                    Escalate incidents to supervisor
                    <span className="block text-xs text-slate-500">Automatically share escalated incidents you actioned.</span>
                  </span>
                  <input type="checkbox" checked={preferences.records.escalateIncidents} onChange={updateRecords("escalateIncidents")} />
                </label>
                <label className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-700">
                  <span>
                    Include operations digest
                    <span className="block text-xs text-slate-500">Send analytics overview to provincial coordination each Friday.</span>
                  </span>
                  <input type="checkbox" checked={preferences.records.includeOpsDigest} onChange={updateRecords("includeOpsDigest")} />
                </label>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <Smartphone className="h-4 w-4 text-indigo-500" /> Connected apps
              </div>
              <p className="mt-2 text-sm text-slate-600">Review integrations linked to your account.</p>
              <div className="mt-4 space-y-3">
                {connectedApps.map((app) => (
                  <div key={app.id} className="rounded-xl border border-slate-200 p-4 text-sm text-slate-700">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-900">{app.name}</p>
                        <p className="text-xs text-slate-500">{app.description}</p>
                        <p className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                          <Wifi className="h-3.5 w-3.5 text-indigo-500" /> Last sync {app.lastSync}
                        </p>
                      </div>
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                          app.status === "Active"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {app.status}
                      </span>
                    </div>
                    <div className="mt-3 flex gap-2 text-xs">
                      <button className="rounded-lg border border-slate-200 px-3 py-1 font-semibold text-slate-600 hover:bg-slate-50">
                        Manage access
                      </button>
                      <button className="rounded-lg border border-slate-200 px-3 py-1 font-semibold text-slate-600 hover:bg-slate-50">
                        View activity
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <aside className="space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between text-sm font-semibold text-slate-900">
                <span>Active channels</span>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                  {activeNotificationCount}/{totalNotificationCount}
                </span>
              </div>
              <p className="mt-3 text-sm text-slate-600">{channelSummary || "No channels selected."}</p>
              <div className="mt-4 h-2 w-full rounded-full bg-slate-100">
                <div
                  className="h-2 rounded-full bg-emerald-500 transition-all"
                  style={{ width: `${Math.round((activeNotificationCount / totalNotificationCount) * 100)}%` }}
                />
              </div>
              <p className="mt-3 text-xs text-slate-500">
                Escalation policies recommend at least two channels to reach compliance thresholds.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h4 className="text-sm font-semibold text-slate-900">Security posture</h4>
              {securityWarnings.length === 0 ? (
                <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
                  <CheckCircle2 className="mb-2 h-4 w-4" /> Account meets recommended controls.
                </div>
              ) : (
                <ul className="mt-3 space-y-2 text-sm text-amber-700">
                  {securityWarnings.map((warning) => (
                    <li key={warning} className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
                      <AlertTriangle className="mt-1 h-4 w-4" />
                      <span>{warning}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h4 className="text-sm font-semibold text-slate-900">Workspace summary</h4>
              <ul className="mt-3 space-y-2 text-sm text-slate-600">
                <li>Theme: {preferences.workspace.theme === "system" ? "Match device" : preferences.workspace.theme}</li>
                <li>Density: {preferences.workspace.density === "compact" ? "Compact" : "Comfortable"}</li>
                <li>Dashboard refresh: {preferences.workspace.autoRefresh ? "Enabled" : "Manual"}</li>
                <li>Beta modules: {preferences.workspace.showBeta ? "Opted-in" : "Hidden"}</li>
              </ul>
            </div>
          </aside>
        </form>

        <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-6 py-4">
          <p className="text-xs text-slate-500">Preferences sync to PHMIS Mobile, Teams notifications, and compliance audit exports.</p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleReset}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
            >
              Reset preferences
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-transparent px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-500 disabled:opacity-60"
              disabled={status === "saving"}
            >
              Save preferences
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
